import { enqueueSnackbar } from 'notistack';
import { exception } from '@erikdakoda/log/exception';
import { getErrorMessage } from '@erikdakoda/log/getErrorMessage';

/**
 * Every query and mutation failure reaches Sentry. A failed load also gets the app-wide snack; a
 * failed save gets its snack from the screen that made it.
 *
 * A failed load needs the app-wide snack because screens rarely catch one: a grid whose `where`
 * clause the server rejects keeps showing its previous rows and its previous count, so a failed
 * filter is indistinguishable from one that legitimately matched nothing.
 *
 * A failed save shows one error snack: the screen's own, through `useException()`. An app-wide
 * snack on top of it showed the same failure twice.
 *
 * Reporting and notifying are deliberately separated. Every error is reported, always; only the
 * toast is deduplicated, because one root cause fans out into many identical errors — invalidating
 * a model re-runs its `findMany`, `count` and several `groupBy` queries at once, and a window-focus
 * refetch replays all of them again. Sentry wants each occurrence; the user wants to be told once.
 */

const DEDUPE_WINDOW_MS = 10_000;

/** Long ORM validation errors are unreadable in a snackbar; keep the actionable head. */
const MAX_TOAST_LENGTH = 200;

export type ErrorReporterOptions = {
  /** How long an identical message stays suppressed. */
  dedupeWindowMs?: number;
  /** Injectable clock, for tests. */
  now?: () => number;
  /** Injectable sink, for tests — defaults to a notistack error snackbar. */
  notify?: (message: string) => void;
  /** Injectable Sentry/console reporter, for tests. */
  report?: (error: unknown, context: string) => string;
};

export type ErrorReporter = (error: unknown, context: string, lead: string) => void;

function defaultNotify(message: string): void {
  // The QueryClient is constructed at module scope, so this can be reached during SSR/prerender
  // where there is no SnackbarProvider mounted.
  if (typeof window === 'undefined') return;
  enqueueSnackbar(message, {
    variant: 'error',
    anchorOrigin: { vertical: 'top', horizontal: 'center' },
    autoHideDuration: 8000,
  });
}

function truncate(message: string): string {
  return message.length > MAX_TOAST_LENGTH ? `${message.slice(0, MAX_TOAST_LENGTH - 1)}…` : message;
}

/**
 * Builds a reporter with its own dedupe state. Exported so tests can drive it with a fake clock
 * instead of waiting out the real window.
 */
export function createErrorReporter(options: ErrorReporterOptions = {}): ErrorReporter {
  const {
    dedupeWindowMs = DEDUPE_WINDOW_MS,
    now = () => Date.now(),
    notify = defaultNotify,
    report = exception,
  } = options;

  const lastShownAt = new Map<string, number>();

  return function reportError(error: unknown, context: string, lead: string): void {
    // Always — the Sentry breadcrumb must not depend on whether a toast was shown.
    report(error, context);

    const detail = getErrorMessage(error);
    const message = `${lead}: ${detail}`;
    const at = now();

    const previous = lastShownAt.get(message);
    if (previous !== undefined && at - previous < dedupeWindowMs) return;

    // Drop entries that can no longer suppress anything, so a long session with many distinct
    // failures does not grow the map without bound.
    for (const [seen, when] of lastShownAt) {
      if (at - when >= dedupeWindowMs) lastShownAt.delete(seen);
    }
    lastShownAt.set(message, at);

    notify(truncate(message));
  };
}

const reportError = createErrorReporter();

/** A failed load: reported, and shown in the app-wide snack. */
export function handleQueryError(error: unknown): void {
  reportError(error, 'QueryCache error', 'Could not load data');
}

/**
 * Reports a failed save with no snack. Exported so tests can inject the reporter.
 * @param error What the failed mutation threw.
 * @param report Sends the error to Sentry and the console.
 */
export function reportMutationError(
  error: unknown,
  report: (error: unknown, context: string) => string,
): void {
  report(error, 'MutationCache error');
}

/** A failed save: reported only. The screen that made the save shows its own error snack. */
export function handleMutationError(error: unknown): void {
  reportMutationError(error, exception);
}
